import type {TFunction} from "i18next";

import type {GeoRefAddress} from "@reva/shared/geo-reference";
import type {RevaLanguage} from "@reva/shared/i18n";
import {formatIndex, formatPricePerArea} from "@reva/shared/number-format";
import {
    type AggregatedTimelineData,
    type AggregatedTimelineValue,
    type TimelineRegionalLevel,
    type TimelineSegment,
    TimelineSegmentType,
} from "@reva/shared/timelines";
import type {
    ChartData,
    ChartDataset,
    ChartOptions,
    LegendItem,
    LegendOptions,
    TooltipCallbacks,
    TooltipLabelStyle,
    TooltipOptions,
} from "chart.js";
import {Legend} from "chart.js";
import type {DeepPartial} from "utility-types";

import type {RevaChartStyle} from "./chartUtils";
import {getColor} from "./chartUtils";
import {REGIONAL_LEVEL_DATA} from "./timelineAggregation";
import {getSupportedRegionalLevels, resolveRegionalLevel, resolveSegment} from "./timelineUtil";

type LineChartOptions = ChartOptions<"line">;

/**
 * Get Timeline Chart.
 */
export function getTimelineChartData(
    aggregatedTimelineData: AggregatedTimelineData | null,
    segment: TimelineSegment | null,
    indexed: boolean,
    regionalLevel: TimelineRegionalLevel | null,
    address: GeoRefAddress,
    chartStyle: RevaChartStyle,
    t: TFunction,
): ChartData<"line"> | null {
    const segmentAggregation = resolveSegment(aggregatedTimelineData, segment);
    if (!segmentAggregation) return null;

    const keyValue: keyof AggregatedTimelineValue = indexed ? "index" : "value";
    const keyValueLow = `${keyValue}Low` as keyof AggregatedTimelineValue;
    const keyValueHigh = `${keyValue}High` as keyof AggregatedTimelineValue;
    const datasets: ChartDataset<"line">[] = [];
    const yearAsDate = (year: number): string => `${year}-01-01`;

    if (regionalLevel) {
        const timeLine = resolveRegionalLevel(aggregatedTimelineData, segment, regionalLevel);
        if (!timeLine) return null;

        datasets.push({
            label: indexed ? t("market.timelines.index") : t("market.timelines.price"),
            borderColor: getColor(chartStyle.colorAvg),
            fill: false,
            tension: chartStyle.tension,
            data: timeLine.map((tl) => {
                return {
                    x: yearAsDate(tl.year),
                    y: tl[keyValue],
                } as any;
            }),
        });
        datasets.push({
            label: t("market.timelines.low"),
            borderColor: getColor(chartStyle.colorLow),
            backgroundColor: getColor(chartStyle.fillLow),
            fill: 0,
            tension: chartStyle.tension,
            pointStyle: false,
            showLine: false,
            data: timeLine.map((tl) => {
                return {
                    x: yearAsDate(tl.year),
                    y: tl[keyValueLow],
                } as any;
            }),
        });
        datasets.push({
            label: t("market.timelines.high"),
            borderColor: getColor(chartStyle.colorHigh),
            backgroundColor: getColor(chartStyle.fillHigh),
            fill: 0,
            tension: chartStyle.tension,
            pointStyle: false,
            showLine: false,
            data: timeLine.map((tl) => {
                return {
                    x: yearAsDate(tl.year),
                    y: tl[keyValueHigh],
                } as any;
            }),
        });
    } else {
        let colorIndex = 0;

        const regionalLevels = getSupportedRegionalLevels(aggregatedTimelineData, segment);

        for (const regionalLevel of regionalLevels) {
            const timeLine = resolveRegionalLevel(aggregatedTimelineData, segment, regionalLevel);
            if (!timeLine) continue;
            const {address_field, nameI18n} = REGIONAL_LEVEL_DATA[regionalLevel];

            datasets.push({
                label: t(nameI18n) + (address_field ? ` ${address[address_field]}` : ""),
                borderColor: getColor(chartStyle.colors[colorIndex++ % chartStyle.colors.length]),
                tension: chartStyle.tension,
                data: timeLine.map((tl) => {
                    return {
                        x: yearAsDate(tl.year),
                        y: tl[keyValue],
                    } as any;
                }),
            });
        }
    }
    return {datasets};
}

/**
 * Get Timeline Chart options.
 */
export function getTimelineChartOptions(
    segmentType: TimelineSegmentType,
    indexed: boolean,
    regionalLevel: TimelineRegionalLevel,
    chartStyle: RevaChartStyle,
    t: TFunction,
    language: RevaLanguage,
): LineChartOptions {
    let fmtNumber: (value: string | number) => string;
    if (indexed) {
        fmtNumber = (value: string | number) => formatIndex(value as number, language);
    } else {
        fmtNumber = (value: string | number) =>
            formatPricePerArea(value as number, language, segmentType === TimelineSegmentType.SALE);
    }

    const buildTooltipOptions = (): DeepPartial<TooltipOptions<"line">> => {
        const labelCallback: TooltipCallbacks<"line">["label"] = (context) => {
            let label = context.dataset.label || "";

            if (label) {
                label += ": ";
            }
            if (context.parsed.y !== null) {
                label += fmtNumber(context.parsed.y);
            }
            return label;
        };
        if (regionalLevel) {
            // Only show first dataset (value, Average) in Tooltip
            const filterFn: TooltipOptions<"line">["filter"] = (context) => {
                return context.datasetIndex === 0;
            };
            // Show span after value label
            const afterLabelCallback: TooltipCallbacks<"line">["afterLabel"] = (context) => {
                if (context.datasetIndex !== 0) return "";
                const item1: any = context?.chart?.config?.data?.datasets?.[1].data?.[context.dataIndex];
                const item2: any = context?.chart?.config?.data?.datasets?.[2].data?.[context.dataIndex];
                const low = fmtNumber(item1.y);
                const high = fmtNumber(item2.y);
                return `${t("market.timelines.span")}: ${low} - ${high}`;
            };

            return {
                // Show no colored boxes in tooltip (only 1 dataset visible)
                displayColors: false,
                // Only show first dataset (value, Average) in Tooltip
                filter: filterFn,
                callbacks: {
                    label: labelCallback,
                    afterLabel: afterLabelCallback,
                },
            };
        } else {
            const labelColorCallback: TooltipCallbacks<"line">["labelColor"] = (context): TooltipLabelStyle => {
                return {
                    borderColor: chartStyle.tooltipColorBoxBorder
                        ? getColor(chartStyle.tooltipColorBoxBorderColor)
                        : (context.dataset.borderColor as string),
                    backgroundColor: context.dataset.borderColor as string,
                    borderWidth: chartStyle.tooltipColorBoxBorder,
                    borderRadius: 0,
                };
            };
            return {
                boxPadding: 4,
                boxHeight: 4 + chartStyle.tooltipColorBoxBorder,
                boxWidth: 20 + 2 * chartStyle.tooltipColorBoxBorder,
                callbacks: {
                    label: labelCallback,
                    labelColor: labelColorCallback,
                },
            };
        }
    };

    const buildLegendOptions = (): DeepPartial<LegendOptions<"line">> => {
        if (regionalLevel) {
            // Show no legend
            return {
                display: false,
            };
        }
        return {
            position: "bottom",
            labels: {
                usePointStyle: true,
                pointStyle: "line",
                //borderWidth: 5,
                pointStyleWidth: 20,
                generateLabels: (chart) => {
                    return Legend?.defaults?.labels.generateLabels(chart).map((label: LegendItem) => {
                        return {...label, lineWidth: 4};
                    });
                },
            },
        };
    };

    return {
        maintainAspectRatio: false,
        aspectRatio: 0.6,
        animation: false,
        interaction: {
            mode: "x",
            intersect: false,
        },
        scales: {
            x: {
                type: "timeseries",
                ticks: {
                    color: getColor(chartStyle.ticks),
                },
                grid: {
                    color: getColor(chartStyle.grid),
                },
                time: {
                    unit: "year",
                    tooltipFormat: "yyyy",
                },
            },
            y: {
                beginAtZero: true,
                grace: "10%",
                ticks: {
                    color: getColor(chartStyle.ticks),
                    callback: fmtNumber,
                },
                grid: {
                    color: getColor(chartStyle.grid),
                },
            },
        },
        plugins: {
            legend: buildLegendOptions(),
            tooltip: buildTooltipOptions(),
        },
    };
}
